/*
 * Keeps the preview pane the same height as the write pane after the author
 * drags the textarea's resize handle.
 *
 * Why this is not CSS
 * -------------------
 * In split view the two panes are grid siblings, and by default the PREVIEW
 * governs the row: it caps itself at 40rem and the textarea stretches to match.
 * Dragging the textarea taller grows the row, but the preview cannot follow,
 * because the same max-height that sets the default also clamps the stretch.
 *
 * The relationship therefore has to invert once the author drags - preview
 * drives the row before, textarea drives it after - and "after the author
 * dragged" is state, which CSS has no way to express. Both CSS-only attempts
 * were measured in a browser first: dropping the cap let a long article drive
 * the row to 9000px, and the height:0/min-height:100% trick collapsed the
 * preview to 34px. Hence this.
 *
 * The signal is the inline height the browser writes when the handle is
 * dragged. No drag means no inline height, which means this function does
 * nothing at all - so the default layout is not merely preserved, it is
 * untouched code.
 *
 * Heights are copied as the CSS string rather than through offsetHeight: it is
 * exactly what the author dragged to, it needs no rounding, and it does not
 * depend on a laid-out box (which is also what makes this testable in jsdom,
 * where offsetHeight is always 0).
 */

/**
 * Mirrors `textarea`'s dragged height onto `preview`, or clears the mirror when
 * the textarea has no dragged height. Safe to call repeatedly.
 */
export function syncPreviewHeight(
  textarea: HTMLElement | null,
  preview: HTMLElement | null,
): void {
  if (!textarea || !preview) return;

  const dragged = textarea.style.height;
  if (!dragged) {
    // Back to the stylesheet's defaults. Assigning '' removes the property
    // rather than setting it to something.
    preview.style.height = '';
    preview.style.maxHeight = '';
    textarea.style.flexGrow = '';
    return;
  }

  /*
   * flex-grow off, which is what makes SHRINKING work.
   *
   * The textarea is `flex: 1 1 auto` so that it fills the pane by default. That
   * also means flex-grow keeps the box filled no matter how small a height the
   * drag writes - so dragging downwards changed nothing on screen, the box
   * never resized, and the ResizeObserver watching it never fired. Growing
   * looked fine because the box really did grow. Reported from the field as
   * "reducing the height renders nothing until the mouse is released".
   *
   * With flex-grow at 0 the dragged height is authoritative in both directions.
   */
  textarea.style.flexGrow = '0';

  preview.style.height = dragged;
  // The 40rem cap is what makes the default sensible, and what would otherwise
  // clamp the height just assigned. Lifted only while a dragged height is in
  // force, and restored above when the drag is undone.
  preview.style.maxHeight = 'none';
}
